import { type ServiceParams, unwrapJson } from "../client";
import type {
  CreatePolicyRequest,
  CreateVersionRequest,
  DryRunRequest,
  DryRunResponse,
  PolicyDecisionFilters,
  PolicyDecisionListResponse,
  PolicyListFilters,
  PolicyRuleListResponse,
  PolicyRuleResponse,
  PolicyTemplateResponse,
  PolicyVersionResponse,
} from "./types";

/**
 * Policy service. Wraps both the tenant-scoped read endpoints
 * (``/v1/governance/policies``) and the admin write endpoints
 * (``/v1/admin/governance/policies``). Methods that mutate live
 * under the admin prefix; reads default to the tenant prefix so
 * non-admin users still see the rules they're subject to.
 *
 * Policy semantics:
 *   - Each policy rule has many versions; one version is active.
 *   - "Edit" = create a new version (POST /{rule_id}/versions)
 *     and explicitly activate it (POST /versions/{vid}/activate).
 *   - There is no PATCH on the rule itself, no /enable or /disable
 *     endpoint, and is_enabled is read-only post-create.
 */
const Policies = ({ url, config }: ServiceParams) => {
  const tenantBase = `${url}/v1/governance/policies`;
  const adminBase = `${url}/v1/admin/governance/policies`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async list(filters: PolicyListFilters = {}): Promise<PolicyRuleListResponse> {
      const response = await fetch(`${tenantBase}${buildQuery(filters as Record<string, unknown>)}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<PolicyRuleListResponse>(response);
    },

    async listDecisions(
      filters: PolicyDecisionFilters = {}
    ): Promise<PolicyDecisionListResponse> {
      const response = await fetch(
        `${tenantBase}/decisions${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<PolicyDecisionListResponse>(response);
    },

    async create(body: CreatePolicyRequest): Promise<PolicyVersionResponse> {
      const response = await fetch(adminBase, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<PolicyVersionResponse>(response);
    },

    async createVersion(
      ruleId: string,
      body: CreateVersionRequest
    ): Promise<PolicyVersionResponse> {
      const response = await fetch(`${adminBase}/${ruleId}/versions`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<PolicyVersionResponse>(response);
    },

    async activateVersion(
      ruleId: string,
      versionId: string
    ): Promise<PolicyVersionResponse> {
      const response = await fetch(
        `${adminBase}/${ruleId}/versions/${versionId}/activate`,
        { method: "POST", headers: config() }
      );
      return unwrapJson<PolicyVersionResponse>(response);
    },

    async delete(ruleId: string): Promise<void> {
      const response = await fetch(`${adminBase}/${ruleId}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok) {
        // 204 has no body; only unwrap on error to surface the message.
        return unwrapJson<void>(response);
      }
    },

    async dryRun(body: DryRunRequest): Promise<DryRunResponse> {
      const response = await fetch(`${adminBase}/dry-run`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<DryRunResponse>(response);
    },

    async listTemplates(): Promise<PolicyTemplateResponse[]> {
      const response = await fetch(`${adminBase}/templates`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<PolicyTemplateResponse[]>(response);
    },

    async adminList(
      filters: PolicyListFilters = {}
    ): Promise<PolicyRuleListResponse> {
      const response = await fetch(`${adminBase}${buildQuery(filters as Record<string, unknown>)}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<PolicyRuleListResponse>(response);
    },

    // Tenant-scoped read for a single rule. The endpoint /v1/governance/
    // policies/{id} is not separately exposed by the backend yet — we
    // derive a "fetch by id" from list + .find on the page to keep
    // the surface honest.
    async findById(id: string): Promise<PolicyRuleResponse | null> {
      const page = await this.list({ limit: 200 });
      return page.items.find((p) => p.id === id) ?? null;
    },
  };
};

export default Policies;
